import React, { useState, useEffect, useMemo } from "react";
import {
  Stack,
  TextField,
  FormControl,
  Select,
  MenuItem,
  InputAdornment,
  Typography,
  InputLabel,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

const HEALTH_STATUSES = ["All", "Well", "Acute", "Chronic", "At Risk", "Complex", "End of Life", "No Status"];
const VISIT_TIMEFRAMES = ["All", "12+ Months", "9 Months", "6 Months", "3 Months", "<3 Months"];

/**
 * Barra de filtros para la worklist de pacientes.
 * Search (debounced), healthStatus, lastVisit, provider.
 */
export default function PatientFilters({ filters, onFilterChange, pcps }) {
  const [searchInput, setSearchInput] = useState(filters.search || "");

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      onFilterChange({ ...filters, search: searchInput });
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]); // eslint-disable-line react-hooks/exhaustive-deps

  const providerOptions = useMemo(() => {
    if (!pcps?.length) return [];
    return pcps.map((p) => ({ npi: p.npi, name: p.name }));
  }, [pcps]);

  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1.5}
      alignItems={{ xs: "stretch", sm: "center" }}
      sx={{ flexWrap: "wrap" }}
    >
      {/* Search */}
      <TextField
        size="small"
        placeholder="Search patient..."
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: "text.secondary" }} />
              </InputAdornment>
            ),
          },
        }}
        sx={{
          minWidth: 200,
          "& .MuiOutlinedInput-root": { borderRadius: 2 },
        }}
      />

      {/* Health Status */}
      <FormControl size="small" sx={{ minWidth: 130 }}>
        <InputLabel sx={{ fontSize: "0.8rem" }}>Status</InputLabel>
        <Select
          value={filters.healthStatus || "All"}
          label="Status"
          onChange={(e) => onFilterChange({ ...filters, healthStatus: e.target.value })}
          sx={{ borderRadius: 2, fontSize: "0.8rem" }}
        >
          {HEALTH_STATUSES.map((s) => (
            <MenuItem key={s} value={s}>
              <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>{s}</Typography>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Last Visit */}
      <FormControl size="small" sx={{ minWidth: 130 }}>
        <InputLabel sx={{ fontSize: "0.8rem" }}>Last Visit</InputLabel>
        <Select
          value={filters.lastVisit || "All"}
          label="Last Visit"
          onChange={(e) => onFilterChange({ ...filters, lastVisit: e.target.value })}
          sx={{ borderRadius: 2, fontSize: "0.8rem" }}
        >
          {VISIT_TIMEFRAMES.map((v) => (
            <MenuItem key={v} value={v}>
              <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>{v}</Typography>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Provider */}
      {providerOptions.length > 1 && (
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel sx={{ fontSize: "0.8rem" }}>Provider</InputLabel>
          <Select
            value={filters.provider || "All"}
            label="Provider"
            onChange={(e) => onFilterChange({ ...filters, provider: e.target.value })}
            sx={{ borderRadius: 2, fontSize: "0.8rem" }}
          >
            <MenuItem value="All">
              <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>All Providers</Typography>
            </MenuItem>
            {providerOptions.map((p) => (
              <MenuItem key={p.npi} value={p.npi}>
                <Typography variant="body2" sx={{ fontSize: "0.8rem" }} noWrap>{p.name}</Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    </Stack>
  );
}
